import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API_BASE,
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem("fstudy_token");
  if (token) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  // Fase 6: propaga la empresa activa en cada request (si existe).
  const cid = localStorage.getItem("fstudy_active_company_id");
  if (cid) {
    config.headers = config.headers || {};
    config.headers["X-Company-Id"] = cid;
  }
  return config;
});

// Endpoints donde la UI ya maneja errores explícitamente y no queremos toast duplicado.
const SILENT_ERROR_PATHS = [
  "/auth/login",
  "/auth/me",
  "/auth/logout",
];

function shouldToastError(err) {
  if (!err.config) return false;
  const url = err.config.url || "";
  // Silenciar endpoints de auth de sesión (UI ya muestra su propio mensaje).
  if (SILENT_ERROR_PATHS.some((p) => url.endsWith(p) || url.includes(p))) return false;
  // Silenciar 401 (redirect al login se encarga)
  if (err.response?.status === 401) return false;
  return true;
}

let lastToastAt = 0;
function throttledToast(msg, kind = "error") {
  const now = Date.now();
  if (now - lastToastAt < 1500) return; // evita spam
  lastToastAt = now;
  const prefixed = msg.startsWith("FStudy:") ? msg : `FStudy: ${msg}`;
  if (kind === "error") toast.error(prefixed);
  else toast(prefixed);
}

api.interceptors.response.use(
  (r) => r,
  async (err) => {
    // Fase 25.1 · Race condition al montar la app: si el request salió antes
    // de que CompanyProvider setee `fstudy_active_company_id`, el backend
    // responde 400 "Falta el encabezado X-Company-Id". Reintentamos UNA sola
    // vez con 400ms de delay — para entonces el context ya cargó la empresa
    // activa y el interceptor de request agregará el header correctamente.
    const detail = err.response?.data?.detail;
    const isMissingCompanyHeader = err.response?.status === 400
      && typeof detail === "string"
      && detail.includes("Falta el encabezado X-Company-Id");
    if (isMissingCompanyHeader && !err.config?.__retried_missing_company) {
      await new Promise((res) => setTimeout(res, 400));
      const cid = localStorage.getItem("fstudy_active_company_id");
      if (cid) {
        err.config.__retried_missing_company = true;
        err.config.headers = { ...(err.config.headers || {}), "X-Company-Id": cid };
        return api.request(err.config);
      }
    }

    // Bubble up but on 401 clear token so ProtectedRoute redirects
    if (err.response && err.response.status === 401) {
      const path = window.location.pathname;
      const detail = err.response?.data?.detail || "";
      const sessionKilled = typeof detail === "string" && detail.includes("otro dispositivo");
      if (path !== "/login") {
        localStorage.removeItem("fstudy_token");
        // Fase 13 · propagar motivo del cierre al Login para mostrarlo elegante.
        if (sessionKilled) {
          localStorage.setItem(
            "fstudy_login_flash",
            "FStudy: Sesión cerrada porque se inició sesión en otro dispositivo."
          );
          // Redirigir al login inmediatamente para no dejar UI zombie.
          try { window.location.href = "/login"; } catch (_) { /* ignore */ }
        }
      }
    }

    // Global toast en español para errores 5xx / red / timeout
    if (shouldToastError(err)) {
      if (!err.response) {
        // Sin respuesta: puede ser red, timeout o CORS
        throttledToast("Sin conexión con el servidor. Verifique su red e intente nuevamente.");
      } else if (err.response.status >= 500) {
        throttledToast("El servidor no pudo procesar la solicitud. Intente de nuevo en unos segundos.");
      } else if (err.response.status === 429) {
        throttledToast("Demasiadas solicitudes. Espere un momento antes de continuar.");
      }
    }

    return Promise.reject(err);
  }
);

export function formatApiErrorDetail(detail) {
  if (detail == null) return "FStudy: ocurrió un error. Intente nuevamente.";
  let msg;
  if (typeof detail === "string") msg = detail;
  else if (Array.isArray(detail)) {
    msg = detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  } else if (detail && typeof detail.msg === "string") msg = detail.msg;
  else msg = String(detail);
  return msg.startsWith("FStudy:") ? msg : `FStudy: ${msg}`;
}

export default api;
